import React, { useEffect, useRef, useState } from "react";
import { Formik, Field, Form } from "formik";
import {
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    FieldWrappers,
    ModalHeader,
} from "../Styles/InputStyles";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import { MdClose, MdEdit } from "react-icons/md";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "../SinglePropSelectModal";
import Config, {
    assetAssignStatus,
    assetAssignTo,
} from "../../Config";
import SelectOptionModal from "../SelectOptionModal";

const EditAssignmentDetails = ({
    setIsEditAssignmentDetails,
    property,
    editAssignPropertyMutate,
    editAssignPropertyLoading,
    selectedData,
    selectedPropertyAssets,
    selectedProperty,
    staffList,
}) => {
    const formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [selectedProp, setSelectedProperty] = useState(selectedProperty);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isAssignStatusModalOpen, setIsAssignStatusModalOpen] = useState(false);
    const [isAssignStaffModalOpen, setIsAssignStaffModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        setSelectedProperty(selectedProperty);
    }, [selectedProperty]);

    const initialValues = {
        assignedProperty:
            selectedProperty?.propId ??
            selectedProperty?.id ??
            "",
        assignTo:
            selectedProperty?.assignedTo ??
            "",
        currentStatus:
            selectedProperty?.status ??
            "",
        count:
            selectedProperty?.unitCount ??
            (selectedData?.type === 10 ? 1 : ""),
        assetId:
            selectedProperty?.assetId ??
            "",
        id:
            selectedProperty?.id ??
            "",
        assignToId:
            selectedProperty?.assignedToId ??
            "",
    };

    const assignToOptions = assetAssignTo
        .filter((role) => {
            // Asset type 10 cannot be assigned to tenant
            if (selectedData?.type === 10) {
                return (
                    role.value !==
                    Config.ASSET_ASSIGNED_TO.TENANT
                );
            }

            return true;
        })
        .map((role) => ({
            label: role.label,
            value: role.value,
        }));

    const assignStatusOptions = assetAssignStatus.map(
        (role) => ({
            label: role.label,
            value: role.value,
        })
    );
    const assignToStaffOptions = (staffList || []).map(
        (role) => ({
            label: role.name,
            value: role.id,
        })
    );

    const validationSchema = Yup.object({
        assignedProperty: Yup.string()
            .required("Select Property"),

        assignTo: Yup.string()
            .required("Select Assign To"),

        currentStatus: Yup.string()
            .required("Select Current Status"),

        assignToId: Yup.string().test(
            "assignToId-required",
            "Please select staff member",
            function (value) {
                const { assignTo } = this.parent;

                if (
                    Number(assignTo) ===
                    Number(
                        Config.ASSET_ASSIGNED_TO.STAFF
                    )
                ) {
                    return (
                        value !== undefined &&
                        value !== null &&
                        value.toString().trim().length > 0
                    );
                }

                return true;
            }
        ),

        // IMPORTANT:
        // Type 10 does not show count field,
        // therefore count should NOT be required.
        count:
            selectedData?.type === 10
                ? Yup.number().nullable()
                : Yup.number()
                    .typeError(
                        "Count must be a number"
                    )
                    .required(
                        "Count is required"
                    )
                    .min(
                        0,
                        "Units should not be less than 0"
                    )
                    .max(
                        10000,
                        "Units should be less than 10000"
                    ),
    });

    const handleSubmit = (values) => {
        const isStaff =
            Number(values.assignTo) ===
            Number(Config.ASSET_ASSIGNED_TO.STAFF);

        const payload = {
            assetId:
                selectedProperty?.assetId ??
                values.assetId,

            propId: Number(
                values.assignedProperty
            ),

            // Type 10 always has quantity 1
            count:
                selectedData?.type === 10
                    ? 1
                    : Number(values.count),

            status: Number(
                values.currentStatus
            ),

            assignedTo: Number(
                values.assignTo
            ),

            assignedToId: isStaff
                ? Number(values.assignToId)
                : 0,

            id:
                selectedProperty?.id ??
                values.id,
        };

        editAssignPropertyMutate(payload);
    };

    const handleSelectedProperty = (
        selectedOption
    ) => {
        setSelectedProperty(
            selectedOption
        );

        if (formikRef.current) {
            formikRef.current.setFieldValue(
                "assignedProperty",
                selectedOption?.propId ??
                selectedOption?.id ??
                ""
            );

            formikRef.current.setFieldTouched(
                "assignedProperty",
                true,
                false
            );
        }

        setShowPropertyModal(false);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling:
                    "touch",
            }}
        >
            {/* Header */}
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Edit Assignment
                </h1>

                <CloseButton
                    type="button"
                    onClick={() =>
                        setIsEditAssignmentDetails(
                            false
                        )
                    }
                >
                    <MdClose size={24} />
                </CloseButton>
            </ModalHeader>

            <Content>
                <Formik
                    innerRef={formikRef}
                    initialValues={
                        initialValues
                    }
                    onSubmit={handleSubmit}
                    validationSchema={
                        validationSchema
                    }
                    enableReinitialize
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({
                        values,
                        setFieldValue,
                        setFieldTouched,
                        errors,
                        touched,
                    }) => {

                        const getAssignLabel =
                            () => {
                                const selectedAssignTo =
                                    assetAssignTo.find(
                                        (role) =>
                                            Number(
                                                role.value
                                            ) ===
                                            Number(
                                                values?.assignTo
                                            )
                                    );

                                return selectedAssignTo
                                    ? selectedAssignTo.label
                                    : "Select Assign To";
                            };

                        const getAssignStatusLabel =
                            () => {
                                const selectedAssignStatus =
                                    assetAssignStatus.find(
                                        (role) =>
                                            Number(
                                                role.value
                                            ) ===
                                            Number(
                                                values?.currentStatus
                                            )
                                    );

                                return selectedAssignStatus
                                    ? selectedAssignStatus.label
                                    : "Select Assign Status";
                            };

                        const getAssignStaffLabel =
                            () => {
                                const selectedAssignToId =
                                    (
                                        staffList ||
                                        []
                                    ).find(
                                        (role) =>
                                            Number(
                                                role.id
                                            ) ===
                                            Number(
                                                values?.assignToId
                                            )
                                    );

                                return selectedAssignToId
                                    ? selectedAssignToId.name
                                    : "Select Staff";
                            };

                        const handleAssignToSelect =
                            (
                                selectedValue
                            ) => {
                                setFieldValue(
                                    "assignTo",
                                    selectedValue
                                );

                                // Reset staff whenever Assign To changes
                                setFieldValue(
                                    "assignToId",
                                    ""
                                );

                                setFieldTouched(
                                    "assignTo",
                                    true,
                                    false
                                );
                            };

                        const handleAssignStatusSelect =
                            (
                                selectedValue
                            ) => {
                                setFieldValue(
                                    "currentStatus",
                                    selectedValue
                                );

                                setFieldTouched(
                                    "currentStatus",
                                    true,
                                    false
                                );
                            };

                        const handleAssignToStaffSelect =
                            (
                                selectedValue
                            ) => {
                                setFieldValue(
                                    "assignToId",
                                    selectedValue
                                );

                                setFieldTouched(
                                    "assignToId",
                                    true,
                                    false
                                );

                                setIsAssignStaffModalOpen(
                                    false
                                );
                            };

                        const isStaffRequired =
                            Number(
                                values.assignTo
                            ) ===
                            Number(
                                Config.ASSET_ASSIGNED_TO.STAFF
                            );

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                    <Wrapper>
                                        <InputGroup>
                                            <Label>
                                                Select
                                                Property *
                                            </Label>

                                            <CategoryCard
                                                title="Property"
                                                selectedCategory={
                                                    selectedProp
                                                }
                                                onClick={() =>
                                                    setShowPropertyModal(
                                                        true
                                                    )
                                                }
                                            />

                                            {errors.assignedProperty &&
                                                touched.assignedProperty && (
                                                    <ErrorText>
                                                        {
                                                            errors.assignedProperty
                                                        }
                                                    </ErrorText>
                                                )}
                                        </InputGroup>

                                        {selectedData?.type !==
                                            10 && (
                                                <InputGroup>
                                                    <Label htmlFor="count">
                                                        Assign
                                                        Units *
                                                    </Label>

                                                    <FieldWrappers>
                                                        <Field
                                                            type="text"
                                                            id="count"
                                                            name="count"
                                                            inputMode="numeric"
                                                            autoComplete="off"
                                                            value={
                                                                values.count
                                                            }
                                                            onChange={(
                                                                e
                                                            ) => {
                                                                const value =
                                                                    e
                                                                        .target
                                                                        .value;

                                                                if (
                                                                    /^[0-9]*$/.test(
                                                                        value
                                                                    )
                                                                ) {
                                                                    setFieldValue(
                                                                        "count",
                                                                        value
                                                                    );
                                                                }
                                                            }}
                                                            maxLength={
                                                                10
                                                            }
                                                        />
                                                    </FieldWrappers>

                                                    {touched.count &&
                                                        errors.count && (
                                                            <ErrorText>
                                                                {
                                                                    errors.count
                                                                }
                                                            </ErrorText>
                                                        )}
                                                </InputGroup>
                                            )}

                                        <InputGroup>
                                            <Label htmlFor="assignTo">
                                                Assign To *
                                            </Label>

                                            <FieldWrappers
                                                onClick={() =>
                                                    setIsAssignModalOpen(
                                                        true
                                                    )
                                                }
                                            >
                                                <Field
                                                    type="text"
                                                    name="assignTo"
                                                    id="assignTo"
                                                    autoComplete="off"
                                                    readOnly
                                                    value={getAssignLabel()}
                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                />

                                                <IconWrapper>
                                                    <MdEdit className="w-3 h-3" />
                                                </IconWrapper>
                                            </FieldWrappers>

                                            {errors.assignTo &&
                                                touched.assignTo && (
                                                    <ErrorText>
                                                        {
                                                            errors.assignTo
                                                        }
                                                    </ErrorText>
                                                )}
                                        </InputGroup>

                                        {isStaffRequired && (
                                            <InputGroup>
                                                <Label htmlFor="assignToId">
                                                    Assign To
                                                    Staff *
                                                </Label>

                                                <FieldWrappers
                                                    onClick={() =>
                                                        setIsAssignStaffModalOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    <Field
                                                        type="text"
                                                        name="assignToId"
                                                        id="assignToId"
                                                        autoComplete="off"
                                                        readOnly
                                                        value={getAssignStaffLabel()}
                                                        className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                    />

                                                    <IconWrapper>
                                                        <MdEdit className="w-3 h-3" />
                                                    </IconWrapper>
                                                </FieldWrappers>

                                                {errors.assignToId &&
                                                    touched.assignToId && (
                                                        <ErrorText>
                                                            {
                                                                errors.assignToId
                                                            }
                                                        </ErrorText>
                                                    )}
                                            </InputGroup>
                                        )}

                                        <InputGroup>
                                            <Label htmlFor="currentStatus">
                                                Current
                                                Status *
                                            </Label>

                                            <FieldWrappers
                                                onClick={() =>
                                                    setIsAssignStatusModalOpen(
                                                        true
                                                    )
                                                }
                                            >
                                                <Field
                                                    type="text"
                                                    name="currentStatus"
                                                    id="currentStatus"
                                                    autoComplete="off"
                                                    readOnly
                                                    value={getAssignStatusLabel()}
                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                />

                                                <IconWrapper>
                                                    <MdEdit className="w-3 h-3" />
                                                </IconWrapper>
                                            </FieldWrappers>

                                            {errors.currentStatus &&
                                                touched.currentStatus && (
                                                    <ErrorText>
                                                        {
                                                            errors.currentStatus
                                                        }
                                                    </ErrorText>
                                                )}
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={() =>
                                            setIsEditAssignmentDetails(
                                                false
                                            )
                                        }
                                    >
                                        Cancel
                                    </SideCancelBtn>

                                    <SubmitBtn
                                        type="submit"
                                        disabled={
                                            editAssignPropertyLoading
                                        }
                                    >
                                        {editAssignPropertyLoading
                                            ? "Submitting..."
                                            : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                <SinglePropSelectModal
                                    isOpen={
                                        showPropertyModal
                                    }
                                    onClose={() =>
                                        setShowPropertyModal(
                                            false
                                        )
                                    }
                                    onSelect={
                                        handleSelectedProperty
                                    }
                                    options={
                                        property || []
                                    }
                                    selectedProperty={
                                        selectedProp
                                    }
                                />

                                <SelectOptionModal
                                    isOpen={
                                        isAssignModalOpen
                                    }
                                    onClose={() =>
                                        setIsAssignModalOpen(
                                            false
                                        )
                                    }
                                    title="Select Assign To"
                                    options={
                                        assignToOptions
                                    }
                                    selectedValue={
                                        values.assignTo
                                    }
                                    onSelect={
                                        handleAssignToSelect
                                    }
                                    searchPlaceholder="Search assign to..."
                                />

                                <SelectOptionModal
                                    isOpen={
                                        isAssignStatusModalOpen
                                    }
                                    onClose={() =>
                                        setIsAssignStatusModalOpen(
                                            false
                                        )
                                    }
                                    title="Select Assign Status"
                                    options={
                                        assignStatusOptions
                                    }
                                    selectedValue={
                                        values.currentStatus
                                    }
                                    onSelect={
                                        handleAssignStatusSelect
                                    }
                                    searchPlaceholder="Search assign status..."
                                />

                                <SelectOptionModal
                                    isOpen={
                                        isAssignStaffModalOpen
                                    }
                                    onClose={() =>
                                        setIsAssignStaffModalOpen(
                                            false
                                        )
                                    }
                                    title="Select Assign To Staff"
                                    options={
                                        assignToStaffOptions
                                    }
                                    selectedValue={
                                        values.assignToId
                                    }
                                    onSelect={
                                        handleAssignToStaffSelect
                                    }
                                    searchPlaceholder="Search assign to..."
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </Content>
        </Container>
    );
};

const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const InputGroup = tw.div`w-full flex flex-col mt-2 md:mt-0`;
const Label = tw.label`text-sm text-gray-600 font-normal mb-1.5`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const CloseButton = tw.button`text-white`;
const Content = tw.div` px-6 h-full flex flex-col justify-between relative`;
const IconWrapper = tw.div`mr-2 flex-shrink-0 p-2 rounded-lg transition-all duration-300 bg-gray-100 text-gray-600`;

export default EditAssignmentDetails;